import RoundTab from "components/RoundTabButtons/RoundTab";
import BackendExportModal from "custom_modules/ReportsNew/BackendExportModal";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import PricingPlan from "../../components/PricingPlan";
import { TabButton, TabButtonContainer } from "../../styledComponents/buttons";
import { CommonGrid, ContainerNew, PageTitle } from "../../styledComponents/common";
import {
  PayrollHistoryTab,
  PayrollHistoryTabContainer,
} from "../../styledComponents/payroll";
import FixedAmount from "./FixedAmount";
import PayrollHistoryTable from "./PayrollHistoryTable";
import PayrollTable from "./PayrollTable";
import PayrollFilter from "./filterContainer";

const Payroll = ({
  officesShortList,
  selectedOrganization,
  paymentHistoryIsLoading,
  getHourlyPaymentHistory,
  getOnetimePaymentHistory,
  getOrganizationMembersShortList,
  getOfficeDetails,
  getPayablesData,
  getOfficesShortList,
  hourlyPaymentHistory,
  onetimePaymentHistory,
  sendOneTimePayment,
  orgMembersShortList,
  officeMembersList,
  oneTimePaymentData,
  clearOneTimePaymentData,
  paymentIsLoading,
  payablesData,
  sendHourlyPayment,
  payablesIsLoading,
  exportPayablesData,
  history,
}) => {
  const { t } = useTranslation();
  const orgType = selectedOrganization?.type || "operation";
  const isOperationType = orgType === "operation";
  const [tabSelect, setTabSelect] = useState("hourly");
  const [startDate, setStartDate] = useState(moment().subtract(6, "days"));
  const [endDate, setEndDate] = useState(moment());
  const [dateLabel, setDateLabel] = useState("");
  const [dateChanged, setDateChanged] = useState(false);

  const [sortType, setSortType] = useState({
    value: "oldToNew",
    label: "Date (Old to New)",
  });
  const [payrollHistoryType, setPayrollHistoryType] = useState("hourly_basis");
  const [officeOptions, setOfficeOptions] = useState([
    {
      label: `(${t("all_teams")})`,
      value: null,
    },
  ]);
  const [selectedOffice, setSelectedOffice] = useState({
    label: `(${t("all_teams")})`,
    value: null,
  });
  const [selectedMember, setSelectedMember] = useState("");

  const [timeFormat, setTimeFormat] = useState("1h5m");
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  // Pricing popup states
  const [pricingPopup, setPricingPopup] = useState(false);
  const [pricingPopupText, setPricingPopupText] = useState(
    "You don't have permission to this feature.",
  );
  const [pricingPopupTitle, setPricingPopupTitle] = useState(
    "Upgrade to paid plan to access this feature",
  );

  const onMemberSelect = person => {
    setCurrentPageNumber(1);
    setSelectedMember(person);
  };
  const toggle = () => {
    setIsExportModalOpen(!isExportModalOpen);
  };

  const pricingPopupToggle = () => {
    setPricingPopup(!pricingPopup);
  };

  const checkPlanAndShowPopup = () => {
    if (
      selectedOrganization?.plan?.nick_name === "standard-free" ||
      selectedOrganization?.plan?.nick_name === "elite-free"
    ) {
      pricingPopupToggle();
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (selectedOrganization?.id) {
      getOfficesShortList({
        organization_id: selectedOrganization.id,
      });
      setSelectedMember("");
      setSortType({
        value: "oldToNew",
        label: "Date (Old to New)",
      });
    }
  }, [selectedOrganization]);

  useEffect(() => {
    if (officesShortList && officesShortList.length > 0) {
      let newArr = [
        {
          label: `(${t("all_teams")})`,
          value: null,
        },
      ];
      officesShortList.forEach((office, i) => {
        newArr.push({
          label: office.name,
          value: office.id,
        });
      });
      setOfficeOptions(newArr);
    }
  }, [officesShortList]);

  const clearFilters = () => {
    setSelectedOffice({
      label: `(${t("all_teams")})`,
      value: null,
    });
    setSelectedMember("");
    setSortType({
      value: "oldToNew",
      label: "Date (Old to New)",
    });
  };

  const changeSortType = type => {
    setSortType(type);
  };

  const onTabSwitch = value => {
    setTabSelect(value);
  };

  const changeHistoryType = value => {
    setPayrollHistoryType(value);
  };

  const onDateSelect = today => {
    let organizationId = selectedOrganization ? selectedOrganization.id : "";
    if (organizationId) {
      const payload = {
        organization_id: organizationId,
        start: today
          ? moment().format("YYYY-MM-DD")
          : moment(startDate).format("YYYY-MM-DD"),
        end: today
          ? moment().format("YYYY-MM-DD")
          : moment(endDate).format("YYYY-MM-DD"),
      };
      if (selectedOffice && selectedOffice.value) {
        payload.office_id = selectedOffice.value;
      }
      if (selectedMember && selectedMember.id) {
        payload.user_ids = [selectedMember.id];
      }
      getPayablesData(payload);
      if (today) {
        setStartDate(moment());
        setEndDate(moment());
      }
    }
  };

  const onDateSelectHistory = today => {
    const organizationId = selectedOrganization ? selectedOrganization.id : "";
    if (organizationId) {
      const payload = {
        organization_id: organizationId,
        start_date: today
          ? moment().format("YYYY-MM-DD")
          : moment(startDate).format("YYYY-MM-DD"),
        end_date: today
          ? moment().format("YYYY-MM-DD")
          : moment(endDate).format("YYYY-MM-DD"),
      };
      if (selectedOffice && selectedOffice.value) {
        payload.office_id = selectedOffice.value;
      }
      if (selectedMember && selectedMember.id) {
        payload.user_ids = [selectedMember.id];
      }
      if (payrollHistoryType === "hourly_basis") {
        getHourlyPaymentHistory(payload);
      } else if (payrollHistoryType === "fixed_amount") {
        getOnetimePaymentHistory(payload);
      }
      if (today) {
        setStartDate(moment());
        setEndDate(moment());
      }
    }
  };

  const changeDateRange = ({ startDate, endDate }) => {
    if (!checkPlanAndShowPopup()) return;
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const getDateLabel = () => {
    const start = moment(startDate).startOf("day");
    const end = moment(endDate).endOf("day"); // force end of day
    const diff = end.diff(start, "days");

    if (diff === 0 && moment().isSame(start, "day")) {
      return "Today selected";
    }
    return diff + 1 + " day" + (diff > 0 ? "s" : "") + " selected";
  };

  const selectLastWeek = () => {
    setStartDate(moment().subtract(6, "days"));
    setEndDate(moment());
    setDateChanged(!dateChanged);

    // if (tabSelect === "history") {
    //   onDateSelectHistory(false);
    // } else {
    //   handleDateChange();
    // }
  };

  const selectLastMonth = () => {
    if (!checkPlanAndShowPopup()) return;
    setStartDate(moment().subtract(29, "days"));
    setEndDate(moment());
    setDateChanged(!dateChanged);

    // if (tabSelect === "history") {
    //   onDateSelectHistory(false);
    // } else {
    //   handleDateChange();
    // }
  };

  const singleDateChange = direction => {
    if (!checkPlanAndShowPopup()) return;
    if (direction === "right") {
      setStartDate(moment(endDate).add(1, "days"));
      setEndDate(moment(endDate).add(1, "days"));
    } else {
      setStartDate(moment(startDate).subtract(1, "days"));
      setEndDate(moment(startDate).subtract(1, "days"));
    }

    setDateChanged(!dateChanged);
  };

  const handleDateChange = () => {
    let organizationId = selectedOrganization ? selectedOrganization.id : "";
    if (organizationId) {
      const payload = {
        organization_id: organizationId,
        start: moment(startDate).format("YYYY-MM-DD"),
        end: moment(endDate).format("YYYY-MM-DD"),
      };
      if (selectedOffice && selectedOffice.value) {
        payload.office_id = selectedOffice.value;
      }
      if (selectedMember && selectedMember.id) {
        payload.user_ids = [selectedMember.id];
      }
      getPayablesData(payload);
    }
  };

  useEffect(() => {
    if (tabSelect === "history") {
      onDateSelectHistory(false);
    } else {
      handleDateChange();
    }
  }, [dateChanged]);

  const handleExportClockInOutList = () => {
    if (selectedOrganization && selectedOrganization.id) {
      const payload = {
        organization_id: selectedOrganization ? selectedOrganization.id : "",
        start: moment(startDate).format("YYYY-MM-DD"),
        end: moment(endDate).format("YYYY-MM-DD"),
        time_format: timeFormat,
      };
      if (selectedOffice && selectedOffice.value) {
        payload.office_id = selectedOffice.value;
      }
      if (selectedMember && selectedMember.id) {
        payload.user_ids = [selectedMember.id];
      }
      exportPayablesData(payload);
    }
    toggle();
  };
  useEffect(() => {
    if (startDate && !endDate) {
      setEndDate(startDate);
    }
    // if (startDate && endDate) {
    //   const maxEndDate = moment(startDate).add(3, 'months');
    //   if (moment(endDate).diff(moment(startDate), 'months', true) > 3) {
    //     setEndDate(maxEndDate);
    //   }
    // }
    const maxEndDate = moment(startDate).add(3, "months");
    if (
      endDate &&
      moment(endDate).diff(moment(startDate), "months", true) > 3
    ) {
      setEndDate(maxEndDate);
    }

    if (startDate && endDate) {
      setDateLabel(getDateLabel());
    }
  }, [startDate, endDate]);

  const toggleSort = () => {
    if (sortType?.value === "oldToNew") {
      changeSortType({ value: "newToOld", label: "Date (New to Old)" });
    } else {
      changeSortType({ value: "oldToNew", label: "Date (Old to New)" });
    }
  };

  const handleExportPayrollHistory = () => {
    if (selectedOrganization && selectedOrganization.id) {
      const payload = {
        organization_id: selectedOrganization.id,
        start_date: moment(startDate).format("YYYY-MM-DD"),
        end_date: moment(endDate).format("YYYY-MM-DD"),
      };
      if (selectedOffice && selectedOffice.value) {
        payload.work_order_id = selectedOffice.value;
      }
      if (selectedMember && selectedMember.id) {
        payload.user_ids = [selectedMember.id];
      }

      if (payrollHistoryType === "hourly_basis") {
        exportHourlyPaymentHistory(payload);
      } else if (payrollHistoryType === "fixed_amount") {
        exportOnetimePaymentHistory(payload);
      }
    }
  };

  //useEffect to handle history tab selection api call
  useEffect(() => {
    const organizationId = selectedOrganization ? selectedOrganization.id : "";
    if (organizationId) {
      const payload = {
        organization_id: organizationId,
        start_date: moment(startDate).format("YYYY-MM-DD"),
        end_date: moment(endDate).format("YYYY-MM-DD"),
      };
      if (selectedOffice && selectedOffice.value) {
        payload.office_id = selectedOffice.value;
      }
      if (selectedMember && selectedMember.id) {
        payload.user_ids = [selectedMember.id];
      }
      if (payrollHistoryType === "fixed_amount") {
        getOnetimePaymentHistory(payload);
      } else {
        getHourlyPaymentHistory(payload);
      }
    }
  }, [payrollHistoryType]);

  return (
    <div className="content">
      <PricingPlan
        isOpen={pricingPopup}
        toggle={pricingPopupToggle}
        popupText={pricingPopupText}
        popupTitle={pricingPopupTitle}
        history={history}
        backRoute={null}
      />
      <BackendExportModal
        isOpen={isExportModalOpen}
        toggle={toggle}
        excelExport={handleExportClockInOutList}
        title="Payroll"
        timeFormat={timeFormat}
        setTimeFormat={setTimeFormat}
      />
      <ContainerNew>
        <PageTitle>Payroll</PageTitle>

        {tabSelect === "hourly" ? (
          <>
            <CommonGrid
              responsiveBreakpoint="1400px"
              responsiveColumns="1fr"
              columns="auto 1fr"
              mobileColumns="auto"
              justifyContent="flex-start"
            >
              {/* col 1 Tabbutton */}
              <CommonGrid
                margin="27px 0 0 0"
                columns="auto auto auto"
                gap="5px"
              >
                <RoundTab
                  text="Hourly Payment"
                  selected={tabSelect === "hourly"}
                  onClick={() => onTabSwitch("hourly")}
                  showIcon={false}
                />
                <RoundTab
                  text="Fixed Amount"
                  selected={tabSelect === "fixed"}
                  onClick={() => onTabSwitch("fixed")}
                  showIcon={false}
                />
                <RoundTab
                  text="History"
                  selected={tabSelect === "history"}
                  onClick={() => onTabSwitch("history")}
                  showIcon={false}
                />
              </CommonGrid>

              {/* col 2 Tab Button end */}
              {/* col  2 Filter  section start */}
              <PayrollFilter
                tabSelect={tabSelect}
                startDate={startDate}
                endDate={endDate}
                dateLabel={dateLabel}
                onDateSelect={onDateSelect}
                changeDateRange={changeDateRange}
                singleDateChange={singleDateChange}
                selectLastWeek={selectLastWeek}
                selectLastMonth={selectLastMonth}
                sortType={sortType}
                changeSortType={changeSortType}
                officeOptions={officeOptions}
                selectedOffice={selectedOffice}
                setSelectedOffice={setSelectedOffice}
                selectedMember={selectedMember}
                setSelectedMember={setSelectedMember}
                margin={"0px"}
                payablesData={payablesData}
                toggleSort={toggleSort}
                toggle={toggle}
              />
            </CommonGrid>
            {/* col  2 Filter section end */}

            <PayrollTable
              tabSelect={tabSelect}
              startDate={startDate}
              endDate={endDate}
              selectedOrganization={selectedOrganization}
              getPayablesData={getPayablesData}
              sortType={sortType}
              payablesData={payablesData}
              sendHourlyPayment={sendHourlyPayment}
              isLoading={payablesIsLoading}
              paymentIsLoading={paymentIsLoading}
            />
          </>
        ) : (
          <>
            <CommonGrid
              justifyContent="flex-start"
              margin="27px 0 0 0"
              columns="auto auto auto"
              gap="5px"
            >
              <RoundTab
                text="Hourly Payment"
                selected={tabSelect === "hourly"}
                onClick={() => onTabSwitch("hourly")}
                showIcon={false}
              />
              <RoundTab
                text="Fixed Amount"
                selected={tabSelect === "fixed"}
                onClick={() => onTabSwitch("fixed")}
                showIcon={false}
              />
              <RoundTab
                text="History"
                selected={tabSelect === "history"}
                onClick={() => onTabSwitch("history")}
                showIcon={false}
              />
            </CommonGrid>
          </>
        )}
        {tabSelect === "fixed" && (
          <FixedAmount
            selectedOrganization={selectedOrganization}
            sendOneTimePayment={sendOneTimePayment}
            getOrganizationMembersList={getOrganizationMembersShortList}
            membersList={orgMembersShortList}
            officeMembersList={officeMembersList}
            officeOptions={officeOptions}
            getOfficeDetails={getOfficeDetails}
            oneTimePaymentData={oneTimePaymentData}
            clearOneTimePaymentData={clearOneTimePaymentData}
            setTabSelect={setTabSelect}
            setHistoryType={setPayrollHistoryType}
            paymentIsLoading={paymentIsLoading}
          />
        )}
        {tabSelect === "history" && (
          <>
            {tabSelect === "history" && (
              <PayrollHistoryTabContainer>
                <PayrollHistoryTab
                  onClick={() => changeHistoryType("hourly_basis")}
                  index={"hourly_basis"}
                  payrollHistoryType={payrollHistoryType}
                >
                  Hourly Basis
                </PayrollHistoryTab>
                <PayrollHistoryTab
                  onClick={() => changeHistoryType("fixed_amount")}
                  index={"fixed_amount"}
                  payrollHistoryType={payrollHistoryType}
                >
                  Fixed Amount
                </PayrollHistoryTab>
              </PayrollHistoryTabContainer>
            )}

            <PayrollFilter
              tabSelect={tabSelect}
              startDate={startDate}
              endDate={endDate}
              dateLabel={dateLabel}
              onDateSelect={onDateSelectHistory}
              changeDateRange={changeDateRange}
              selectLastWeek={selectLastWeek}
              selectLastMonth={selectLastMonth}
              singleDateChange={singleDateChange}
              sortType={sortType}
              changeSortType={changeSortType}
              historyType={payrollHistoryType}
              officeOptions={officeOptions}
              selectedOffice={selectedOffice}
              setSelectedOffice={setSelectedOffice}
              selectedMember={selectedMember}
              setSelectedMember={setSelectedMember}
              margin={"0px 0 0 0"}
              toggleSort={toggleSort}
              handleExportPayrollHistory={handleExportPayrollHistory}
              hourlyPaymentHistory={hourlyPaymentHistory}
            />

            <PayrollHistoryTable
              historyType={payrollHistoryType}
              setHistoryType={setPayrollHistoryType}
              startDate={startDate}
              endDate={endDate}
              sortType={sortType}
              getHourlyPaymentHistory={getHourlyPaymentHistory}
              getOnetimePaymentHistory={getOnetimePaymentHistory}
              hourlyPaymentHistory={hourlyPaymentHistory}
              onetimePaymentHistory={onetimePaymentHistory}
              selectedOrganization={selectedOrganization}
              isLoading={paymentHistoryIsLoading}
              membersList={orgMembersShortList}
            />
          </>
        )}
      </ContainerNew>
    </div>
  );
};

export default Payroll;
